/**
 * Backoffice (and Arabic UI) dates: always Gregorian, never Hijri.
 * `-nu-latn` forces Western digits (1, 2, 3…) even when month names are Arabic.
 */
export function gregorianLocaleForLanguage(language: string): string {
  return language.startsWith('ar') ? 'ar-SA-u-ca-gregory-nu-latn' : 'en-GB';
}

function resolveLocale(language: string): string {
  return gregorianLocaleForLanguage(language);
}

export function formatBackofficeDate(
  iso: string | Date | undefined | null,
  language: string,
  options: Intl.DateTimeFormatOptions = { dateStyle: 'short' },
): string {
  if (iso == null || iso === '') return '';
  const d = typeof iso === 'string' ? new Date(iso) : iso;
  if (Number.isNaN(d.getTime())) return '';
  const locale = resolveLocale(language);
  return new Intl.DateTimeFormat(locale, { ...options, calendar: 'gregory' }).format(d);
}

/** Long Gregorian label in the active UI language. */
export function formatBackofficeDateLong(iso: string | Date | undefined | null, language: string): string {
  return formatBackofficeDate(iso, language, { dateStyle: 'long' });
}

export function formatBackofficeDateTime(iso: string | undefined | null, language: string): string {
  if (iso == null || iso === '') return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const locale = resolveLocale(language);
  return new Intl.DateTimeFormat(locale, {
    calendar: 'gregory',
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(d);
}

/** DD/MM/YYYY display from YYYY-MM-DD (Gregorian). */
export function isoDateToDdMmYyyy(iso: string): string {
  if (!iso || iso.length < 10) return '';
  const [y, m, d] = iso.split('-');
  if (!y || !m || !d) return '';
  return `${d}/${m}/${y}`;
}

/** Parse DD/MM/YYYY (or separators ./-) to YYYY-MM-DD */
export function parseDdMmYyyyToIso(input: string): string {
  const trimmed = input.trim().replace(/\s/g, '');
  if (!trimmed) return '';
  const parts = trimmed.split(/[/.-]/);
  if (parts.length !== 3) return '';
  const d = parseInt(parts[0]!, 10);
  const m = parseInt(parts[1]!, 10);
  const y = parseInt(parts[2]!, 10);
  if (Number.isNaN(d) || Number.isNaN(m) || Number.isNaN(y)) return '';
  if (y < 1900 || y > 2100 || m < 1 || m > 12 || d < 1 || d > 31) return '';
  const month = String(m).padStart(2, '0');
  const day = String(d).padStart(2, '0');
  return `${y}-${month}-${day}`;
}
